<template>
  <router-view v-if="isStandalonePage" />

  <el-container v-else class="app-container">
    <!-- Sidebar -->
    <el-aside width="220px" class="app-aside">
      <div class="logo">
        <el-icon :size="28">
          <Calendar />
        </el-icon>
        <span>
          <span style="background: linear-gradient(135deg, #ff4e50 0%, #f9d423 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 800; margin-right: 2px;">爱教务</span>智能教务
        </span>
      </div>
      <el-menu :default-active="activeMenu" router class="app-menu" background-color="#1e1e2f" text-color="#a0aec0"
        active-text-color="#667eea">
        <el-menu-item index="/portal">
          <el-icon>
            <HomeFilled />
          </el-icon>
          <span>系统入口</span>
        </el-menu-item>

        <el-menu-item v-if="isCourseScheduleSection" index="/course-schedule">
          <el-icon>
            <Notebook />
          </el-icon>
          <span>智能排课</span>
        </el-menu-item>

        <el-menu-item v-if="isScheduleSection" index="/semesters">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>学期管理</span>
        </el-menu-item>
        <el-menu-item v-if="isScheduleSection" index="/teachers">
          <el-icon>
            <User />
          </el-icon>
          <span>教师管理</span>
        </el-menu-item>

        <el-menu-item v-if="isScheduleSection" index="/exams">
          <el-icon>
            <Document />
          </el-icon>
          <span>考试管理</span>
        </el-menu-item>

        <el-menu-item v-if="isScheduleSection" index="/exam-seats">
          <el-icon>
            <Grid />
          </el-icon>
          <span>学生排考</span>
        </el-menu-item>

        <el-menu-item v-if="isScheduleSection" index="/statistics">
          <el-icon>
            <DataLine />
          </el-icon>
          <span>工作量统计</span>
        </el-menu-item>
        <el-menu-item v-if="isAnalysisSection" index="/grades">
          <el-icon>
            <Reading />
          </el-icon>
          <span>成绩管理</span>
        </el-menu-item>
        <el-menu-item v-if="isAnalysisSection" index="/grade-analysis">
          <el-icon>
            <PieChart />
          </el-icon>
          <span>成绩分析</span>
        </el-menu-item>

        <el-menu-item v-if="isScoreQuerySection" index="/score-query">
          <el-icon>
            <Search />
          </el-icon>
          <span>成绩查询发布</span>
        </el-menu-item>

        <el-menu-item v-if="isDivisionSection" index="/division">
          <el-icon>
            <Grid />
          </el-icon>
          <span>智能分班</span>
        </el-menu-item>

        <el-menu-item v-if="isCurrentAdmin" index="/users">
          <el-icon>
            <Setting />
          </el-icon>
          <span>系统管理</span>
        </el-menu-item>

      </el-menu>

      <div class="aside-footer">
        <div class="sidebar-action-btn tutorial-btn" @click="openTutorialDoc">
          <el-icon><Reading /></el-icon>
          <span>操作文档 / 教程</span>
        </div>
        <div class="sidebar-action-btn feedback-btn" @click="showFeedbackDialog">
          <el-icon><ChatDotRound /></el-icon>
          <span>产品反馈</span>
        </div>
      </div>
    </el-aside>

    <!-- Main Content -->
    <el-container>
      <el-header class="app-header">
        <div class="header-content">
          <div class="header-left">
            <h2>{{ $route.meta.title || '爱教务智能教务系统' }}</h2>
          </div>
          <div class="header-actions">
            <el-tag v-if="currentUser?.school" type="success">{{ currentUser.school }}</el-tag>
            <el-tag type="info">{{ currentUser?.username || '未知用户' }}</el-tag>
            
            <!-- VIP Badge -->
            <span class="vip-badge-container">
              <el-tag v-if="!isVipEnabled" class="vip-badge active" effect="dark" type="success" @click="vipDialogVisible = true">
                限免期（全功能免费）
              </el-tag>
              <template v-else>
                <el-tag v-if="vipStatus.isVip" class="vip-badge active" effect="dark" @click="vipDialogVisible = true">
                  <el-icon><GoldMedal /></el-icon> VIP
                </el-tag>
                <el-tag v-else-if="vipStatus.isExpired" class="vip-badge expired" type="info" @click="vipDialogVisible = true">
                  VIP已过期
                </el-tag>
                <el-tag v-else class="vip-badge inactive" type="warning" effect="plain" @click="vipDialogVisible = true">
                  开通VIP
                </el-tag>
              </template>
            </span>

            <el-tag v-if="isCurrentAdmin" type="danger">管理员</el-tag>
            
            <template v-if="isSuperLoggedIn">
              <el-tag type="warning" effect="dark" class="super-login-tag">超级登录模式</el-tag>
              <el-button type="warning" size="small" @click="exitSuperLogin">返回管理员</el-button>
            </template>

            
            
            <el-button link type="primary" @click="showProfileDialog">个人设置</el-button>
            <span class="user-divider">|</span>
            <el-button link type="primary" @click="handleLogout">退出登录</el-button>
          </div>
        </div>
      </el-header>
      <el-main class="app-main">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" :key="route.fullPath" />
          </transition>
        </router-view>
      </el-main>
    </el-container>

    <!-- Global Scheduling Progress (Non-blocking) -->
    <transition name="el-zoom-in-bottom">
      <div v-if="scheduleStore.generating" class="global-progress-card">
        <div class="progress-header">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>正在自动排考...</span>
        </div>
        <div class="progress-body">
          <p class="step-text">{{ scheduleStore.progressStep }}</p>
          <el-progress :percentage="scheduleStore.progressPercent" :stroke-width="8" />
        </div>
      </div>
    </transition>

    <!-- Premium VIP Modal -->
    <el-dialog v-model="vipDialogVisible" title="VIP 会员中心" width="460px" destroy-on-close>
      <div class="vip-modal-content">
        <!-- Golden Membership Card -->
        <div class="vip-card" :class="{ 'card-active': !isVipEnabled || vipStatus.isVip, 'card-expired': isVipEnabled && vipStatus.isExpired, 'card-inactive': isVipEnabled && !vipStatus.isVip && !vipStatus.isExpired }">
          <div class="vip-card-glow"></div>
          <div class="vip-card-header">
            <div class="vip-logo">
              <el-icon :size="28"><GoldMedal /></el-icon>
              <span>{{ !isVipEnabled ? '限免体验' : 'VIP 会员' }}</span>
            </div>
            <div class="vip-status-text">
              {{ !isVipEnabled ? '体验中' : (vipStatus.isVip ? '尊贵会员' : (vipStatus.isExpired ? '已过期' : '未开通')) }}
            </div>
          </div>
          
          <div class="vip-card-body">
            <div class="user-info">
              <span class="username">{{ currentUser?.username }}</span>
              <span class="school">{{ currentUser?.school }}</span>
            </div>
            <div class="vip-validity">
              <span v-if="!isVipEnabled">全功能免费限时体验中</span>
              <span v-else-if="vipStatus.isPermanent">永久会员</span>
              <span v-else-if="vipStatus.isVip">有效期至：{{ formatDate(vipStatus.expiredAt) }}</span>
              <span v-else-if="vipStatus.isExpired">已于 {{ formatDate(vipStatus.expiredAt) }} 过期</span>
              <span v-else>开通后即可解锁专属特权</span>
            </div>
          </div>
        </div>

        <!-- Privileges Section -->
        <div class="vip-privileges">
          <h3>VIP 专享特权</h3>
          <div class="privilege-list">
            <div class="privilege-item">
              <el-icon class="check-icon"><CircleCheck /></el-icon>
              <div class="privilege-desc">
                <h4>智能自动排考</h4>
                <p>一键开启大规模考场自动均衡分派，无需手动调整</p>
              </div>
            </div>
            <div class="privilege-item">
              <el-icon class="check-icon"><CircleCheck /></el-icon>
              <div class="privilege-desc">
                <h4>考场年级隔离与规则自适应</h4>
                <p>支持多校区多学期复杂条件下的隔离及混合模式排考</p>
              </div>
            </div>
            <div class="privilege-item">
              <el-icon class="check-icon"><CircleCheck /></el-icon>
              <div class="privilege-desc">
                <h4>智能教师监考工作量均衡</h4>
                <p>精准控制各教师监考时长差距，确保工作量公平</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Renewal Guidance -->
        <div class="vip-guidance">
          <p v-if="!isVipEnabled" class="guidance-info" style="color: #059669; background: #ecfdf5; border-color: #a7f3d0">当前为限免体验期，您可以免费使用所有 VIP 高级排考功能。</p>
          <p v-else-if="vipStatus.isExpired" class="guidance-warning">您的会员已过期，请联系管理员为您续费。</p>
          <p v-else-if="!vipStatus.isVip" class="guidance-info">排考功能仅限 VIP 会员使用，请联系管理员开通。</p>
          <div class="contact-box">
            <div class="admin-action-btn">
              <el-button type="primary" class="vip-btn" @click="vipDialogVisible = false">我知道了</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Profile & Password Settings Dialog -->
    <el-dialog v-model="profileDialogVisible" title="个人信息与密码设置" width="460px" destroy-on-close>
      <el-form :model="profileForm" :rules="profileRules" ref="profileFormRef" label-position="top">
        <el-form-item label="用户名">
          <el-input v-model="profileForm.username" disabled />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="profileForm.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="所属学校" prop="school">
          <el-input v-model="profileForm.school" placeholder="请输入所属学校全称" />
        </el-form-item>
        
        <div style="margin: 20px 0 10px 0; border-top: 1px dashed #e5e7eb; padding-top: 15px;">
          <h4 style="font-size: 14px; color: #4b5563; font-weight: 600; margin-bottom: 8px;">系统显示偏好</h4>
        </div>
        <el-form-item label="时间显示单位" prop="use_minutes_format">
          <el-switch
            v-model="profileForm.use_minutes_format"
            active-text="分钟 (例: 90分钟)"
            inactive-text="小时 (例: 1.5小时)"
          />
        </el-form-item>
        
        <div style="margin: 20px 0 10px 0; border-top: 1px dashed #e5e7eb; padding-top: 15px;">
          <h4 style="font-size: 14px; color: #4b5563; font-weight: 600; margin-bottom: 8px;">修改密码（如不修改请留空）</h4>
        </div>

        <el-form-item label="原密码" prop="old_password">
          <el-input v-model="profileForm.old_password" type="password" show-password placeholder="修改密码时必须输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="new_password">
          <el-input v-model="profileForm.new_password" type="password" show-password placeholder="请输入新密码（至少6位）" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirm_password">
          <el-input v-model="profileForm.confirm_password" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="profileDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitProfile" :loading="profileSubmitting">保存</el-button>
      </template>
    </el-dialog>

    <!-- Feedback Dialog -->
    <el-dialog v-model="feedbackDialogVisible" title="意见与反馈" width="500px" destroy-on-close>
      <el-form :model="feedbackForm" :rules="feedbackRules" ref="feedbackFormRef" label-position="top">
        <el-form-item label="反馈类型" prop="feedback_type">
          <el-radio-group v-model="feedbackForm.feedback_type">
            <el-radio-button label="bug">🐛 功能问题</el-radio-button>
            <el-radio-button label="feature">✨ 需求想法</el-radio-button>
            <el-radio-button label="suggestion">💡 意见建议</el-radio-button>
            <el-radio-button label="other">❓ 其它</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="标题 / 简述" prop="title">
          <el-input v-model="feedbackForm.title" placeholder="可选，一句话简述您的反馈（如：排考希望支持导出PDF）" />
        </el-form-item>
        <el-form-item label="详细内容" prop="content">
          <el-input v-model="feedbackForm.content" type="textarea" :rows="5" placeholder="请详细描述您遇到的问题、操作步骤或想要的功能建议..." />
        </el-form-item>
        <el-form-item label="联系方式" prop="contact">
          <el-input v-model="feedbackForm.contact" placeholder="可选，微信/手机/邮箱，方便我们与您联系" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="feedbackDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitFeedback" :loading="feedbackSubmitting">提交反馈</el-button>
      </template>
    </el-dialog>
  </el-container>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Calendar, User, Document, DataLine, Loading, Reading, Setting, PieChart, GoldMedal, CircleCheck, HomeFilled, ChatDotRound, Grid, Notebook, Clock, Search } from '@element-plus/icons-vue'
import { useScheduleStore } from '@/stores/schedule'
import { authApi, feedbackApi } from '@/api'
import { clearAuth, getUser, isAdmin, setUser, isImpersonating, stopImpersonation, getVipStatus } from '@/utils/auth'
import { ElMessage } from 'element-plus'

const scheduleStore = useScheduleStore()
const route = useRoute()
const router = useRouter()

const isStandalonePage = computed(() => !!route.meta.standalone)
const schedulePaths = ['/semesters', '/teachers', '/students', '/exams', '/exam-seats', '/statistics', '/schedules', '/exam-clock']
const analysisPaths = ['/grades', '/grade-analysis']
const divisionPaths = ['/division']
const courseSchedulePaths = ['/course-schedule']
const scoreQueryPaths = ['/score-query']

// Remember the last active section in a ref and sessionStorage
const activeSection = ref(sessionStorage.getItem('active_section') || 'schedule')

const isScheduleSection = computed(() => activeSection.value === 'schedule')
const isAnalysisSection = computed(() => activeSection.value === 'analysis')
const isDivisionSection = computed(() => activeSection.value === 'division')
const isCourseScheduleSection = computed(() => activeSection.value === 'course-schedule')
const isScoreQuerySection = computed(() => activeSection.value === 'score-query')

const currentUser = ref(getUser())
const isCurrentAdmin = ref(isAdmin())
const isSuperLoggedIn = ref(isImpersonating())
const vipStatus = ref(getVipStatus())
const vipDialogVisible = ref(false)
const isVipEnabled = ref(true)

const activeMenu = computed(() => {
  const path = route.path
  if (path.startsWith('/course-schedule')) return '/course-schedule'
  if (path.startsWith('/division')) return '/division'
  return path
})

watch(() => route.path, (newPath) => {
  currentUser.value = getUser()
  isCurrentAdmin.value = isAdmin()
  isSuperLoggedIn.value = isImpersonating()
  vipStatus.value = getVipStatus()

  if (courseSchedulePaths.some(path => newPath.startsWith(path))) {
    activeSection.value = 'course-schedule'
    sessionStorage.setItem('active_section', 'course-schedule')
  } else if (schedulePaths.some(path => newPath.startsWith(path))) {
    activeSection.value = 'schedule'
    sessionStorage.setItem('active_section', 'schedule')
  } else if (analysisPaths.some(path => newPath.startsWith(path))) {
    activeSection.value = 'analysis'
    sessionStorage.setItem('active_section', 'analysis')
  } else if (scoreQueryPaths.some(path => newPath.startsWith(path))) {
    activeSection.value = 'score-query'
    sessionStorage.setItem('active_section', 'score-query')
  } else if (divisionPaths.some(path => newPath.startsWith(path))) {
    activeSection.value = 'division'
    sessionStorage.setItem('active_section', 'division')
  } else if (newPath === '/portal') {
    activeSection.value = 'none'
    sessionStorage.setItem('active_section', 'none')
  }
}, { immediate: true })

onMounted(async () => {
  if (currentUser.value) {
    try {
      const user = await authApi.me()
      setUser(user)
      currentUser.value = user
      isCurrentAdmin.value = user.is_admin
      isSuperLoggedIn.value = isImpersonating()
      vipStatus.value = getVipStatus()
      
      const settings = await authApi.getSettings()
      isVipEnabled.value = settings.vip_enabled
    } catch (e) {
      console.error('Failed to sync user profile:', e)
    }
  }
})

const formatDate = (val) => {
  if (!val) return ''
  const d = new Date(val)
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })
}

const exitSuperLogin = () => {
  stopImpersonation()
  ElMessage.success('已退出超级登录，返回管理员账号')
  window.location.href = '/users'
}

const profileDialogVisible = ref(false)
const profileSubmitting = ref(false)
const profileFormRef = ref(null)
const profileForm = ref({
  username: '',
  school: '',
  phone: '',
  old_password: '',
  new_password: '',
  confirm_password: ''
})

const validateConfirmPassword = (rule, value, callback) => {
  if (profileForm.value.new_password && value !== profileForm.value.new_password) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const validateOldPassword = (rule, value, callback) => {
  if (profileForm.value.new_password && !value) {
    callback(new Error('修改密码时必须输入原密码'))
  } else {
    callback()
  }
}

const validatePhone = (rule, value, callback) => {
  if (value && !/^1[3-9]\d{9}$/.test(value)) {
    callback(new Error('手机号格式不正确'))
  } else {
    callback()
  }
}

const profileRules = {
  school: [
    { required: true, message: '请输入所属学校', trigger: 'blur' },
    { min: 2, max: 100, message: '学校名称长度至少 2 位', trigger: 'blur' }
  ],
  phone: [
    { validator: validatePhone, trigger: 'blur' }
  ],
  old_password: [
    { validator: validateOldPassword, trigger: 'blur' }
  ],
  new_password: [
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ],
  confirm_password: [
    { validator: validateConfirmPassword, trigger: 'blur' }
  ]
}

const showProfileDialog = () => {
  const user = getUser()
  profileForm.value = {
    username: user?.username || '',
    school: user?.school || '',
    phone: user?.phone || '',
    use_minutes_format: user?.use_minutes_format || false,
    old_password: '',
    new_password: '',
    confirm_password: ''
  }
  profileDialogVisible.value = true
}

const submitProfile = async () => {
  if (!profileFormRef.value) return
  await profileFormRef.value.validate(async (valid) => {
    if (!valid) return
    profileSubmitting.value = true
    try {
      const payload = {
        school: profileForm.value.school,
        phone: profileForm.value.phone || null,
        use_minutes_format: profileForm.value.use_minutes_format
      }
      
      const passwordChanged = !!profileForm.value.new_password
      if (passwordChanged) {
        payload.old_password = profileForm.value.old_password
        payload.new_password = profileForm.value.new_password
      }
      
      const updatedUser = await authApi.updateProfile(payload)
      setUser(updatedUser)
      currentUser.value = updatedUser
      
      if (passwordChanged) {
        ElMessage.success('密码修改成功，请重新登录')
        profileDialogVisible.value = false
        handleLogout()
      } else {
        ElMessage.success('个人设置更新成功')
        profileDialogVisible.value = false
        window.location.reload()
      }
    } catch (e) {
      console.error(e)
    } finally {
      profileSubmitting.value = false
    }
  })
}

const handleLogout = async () => {
  try {
    await authApi.logout()
  } catch (error) {
    // Ignore logout API errors and clear local session anyway.
  }
  clearAuth()
  router.replace('/login')
}

const openTutorialDoc = () => {
  if (isAnalysisSection.value) {
    window.open('https://my.feishu.cn/docx/CHgfd9TaToPdh7x0VEzcmmaYnlc', '_blank', 'noopener,noreferrer')
  } else if (isDivisionSection.value) {
    router.push('/tutorial?type=division')
  } else if (isCourseScheduleSection.value) {
    router.push('/tutorial?type=course-schedule')
  } else {
    router.push('/tutorial')
  }
}

// ======================== Feedback Logic ========================
const feedbackDialogVisible = ref(false)
const feedbackSubmitting = ref(false)
const feedbackFormRef = ref(null)
const feedbackForm = ref({
  feedback_type: 'bug',
  title: '',
  content: '',
  contact: ''
})

const feedbackRules = {
  feedback_type: [{ required: true, message: '请选择反馈类型', trigger: 'change' }],
  content: [
    { required: true, message: '请输入反馈内容', trigger: 'blur' },
    { min: 5, message: '反馈内容不能少于5个字', trigger: 'blur' }
  ]
}

const showFeedbackDialog = () => {
  feedbackForm.value = {
    feedback_type: 'bug',
    title: '',
    content: '',
    contact: ''
  }
  feedbackDialogVisible.value = true
}

const submitFeedback = async () => {
  if (!feedbackFormRef.value) return
  await feedbackFormRef.value.validate(async (valid) => {
    if (!valid) return
    feedbackSubmitting.value = true
    try {
      await feedbackApi.create({
        feedback_type: feedbackForm.value.feedback_type,
        title: feedbackForm.value.title || `用户反馈 - ${feedbackForm.value.feedback_type}`,
        content: feedbackForm.value.content,
        contact: feedbackForm.value.contact
      })
      ElMessage.success('您的反馈已成功提交！感谢您的支持与建议。')
      feedbackDialogVisible.value = false
    } catch (e) {
      console.error(e)
    } finally {
      feedbackSubmitting.value = false
    }
  })
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body,
#app {
  height: 100%;
  width: 100%;
}

.app-container {
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

.app-aside {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border-right: 1px solid #e5e7eb;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 16px;
  color: #4f46e5;
  font-size: 15px;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.app-menu {
  border: none;
  padding: 10px 0;
  background: transparent;
  flex: 1;
  overflow-y: auto;
}

.app-menu .el-menu-item {
  margin: 4px 8px;
  border-radius: 8px;
  color: #374151;
}

.app-menu .el-menu-item:hover {
  background: rgba(79, 70, 229, 0.08) !important;
  color: #4f46e5;
}

.app-menu .el-menu-item.is-active {
  background: linear-gradient(90deg, rgba(79, 70, 229, 0.15), transparent) !important;
  border-left: 3px solid #4f46e5;
  color: #4f46e5;
}

.app-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e5e7eb;
  padding: 0 24px;
  height: 64px;
}

.header-content {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.app-header h2 {
  color: #1f2937;
  font-size: 18px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.aside-footer {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8f9fa;
}

.sidebar-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sidebar-action-btn .el-icon {
  font-size: 16px;
}

.sidebar-action-btn.tutorial-btn {
  background: rgba(16, 185, 129, 0.06);
  border: 1px solid rgba(16, 185, 129, 0.15);
  color: #059669;
}

.sidebar-action-btn.tutorial-btn:hover {
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.25);
  color: #047857;
}

.sidebar-action-btn.feedback-btn {
  background: rgba(124, 58, 237, 0.06);
  border: 1px solid rgba(124, 58, 237, 0.15);
  color: #7c3aed;
}

.sidebar-action-btn.feedback-btn:hover {
  background: rgba(124, 58, 237, 0.12);
  border-color: rgba(124, 58, 237, 0.25);
  color: #6d28d9;
}

.app-main {
  background: transparent;
  padding: 24px;
  overflow-y: auto;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Element Plus overrides - Light Theme */
.el-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.el-card__header {
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
}

.el-table {
  background: transparent;
  --el-table-bg-color: transparent;
  --el-table-header-bg-color: #f9fafb;
  --el-table-tr-bg-color: transparent;
  --el-table-row-hover-bg-color: rgba(79, 70, 229, 0.05);
  --el-table-header-text-color: #6b7280;
  --el-table-text-color: #374151;
  --el-table-border-color: #e5e7eb;
}

.el-button--primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border: none;
}

.el-button--primary:hover {
  background: linear-gradient(135deg, #4338ca 0%, #6d28d9 100%);
}

.el-dialog {
  background: #ffffff;
  border-radius: 16px;
}

.el-dialog__header {
  border-bottom: 1px solid #e5e7eb;
}

.el-dialog__title {
  color: #1f2937;
}

.el-form-item__label {
  color: #374151;
}

.el-input__wrapper,
.el-select__wrapper {
  background: #f9fafb;
  box-shadow: 0 0 0 1px #d1d5db !important;
}

.el-input__wrapper:hover,
.el-select__wrapper:hover {
  box-shadow: 0 0 0 1px #9ca3af !important;
}

.el-input__wrapper:focus-within,
.el-select__wrapper:focus-within {
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.3) !important;
}

.el-input__inner {
  color: #1f2937;
}

.el-tag--success {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
  color: #059669;
}

.el-tag--info {
  background: rgba(107, 114, 128, 0.1);
  border-color: rgba(107, 114, 128, 0.3);
  color: #4b5563;
}

.el-tag--warning {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.3);
  color: #d97706;
}

.el-tag--danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #dc2626;
}

/* Global Progress Card */
.global-progress-card {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 10000;
  border: 1px solid #e5e7eb;
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #1f2937;
  font-weight: 600;
  font-size: 14px;
}

.progress-body .step-text {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.user-divider {
  margin: 0 8px;
  color: #d1d5db;
}

/* VIP badges and dialog styles */
.vip-badge-container {
  display: inline-flex;
  align-items: center;
  margin: 0 4px;
}

.vip-badge {
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.vip-badge:hover {
  transform: translateY(-1px) scale(1.05);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
}

.vip-badge.active {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: #d97706;
  color: white;
}

.vip-badge.expired {
  background: rgba(156, 163, 175, 0.1);
  border-color: rgba(156, 163, 175, 0.3);
  color: #4b5563;
  text-decoration: line-through;
}

.vip-badge.inactive {
  background: rgba(79, 70, 229, 0.05);
  border-color: rgba(79, 70, 229, 0.2);
  color: #4f46e5;
}

.vip-modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
}

/* Membership Card design */
.vip-card {
  position: relative;
  height: 180px;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: white;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.5s ease;
}

.vip-card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 80%);
  pointer-events: none;
}

.card-active {
  background: linear-gradient(135deg, #1e1b4b 0%, #311042 50%, #11032b 100%);
  border: 1px solid rgba(245, 158, 11, 0.35);
}

.card-active .vip-logo span,
.card-active .vip-status-text {
  background: linear-gradient(to right, #ffe699, #f59e0b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.card-active .vip-logo .el-icon {
  color: #f59e0b;
}

.card-expired {
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  border: 1px solid rgba(156, 163, 175, 0.25);
  opacity: 0.85;
}

.card-inactive {
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  border: 1px solid rgba(79, 70, 229, 0.2);
}

.vip-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.vip-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 1px;
}

.vip-status-text {
  font-size: 14px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
}

.vip-card-body .user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.vip-card-body .username {
  font-size: 24px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.vip-card-body .school {
  font-size: 13px;
  opacity: 0.75;
}

.vip-card-body .vip-validity {
  font-size: 12px;
  opacity: 0.6;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
}

/* Privileges Section */
.vip-privileges {
  background: #f8fafc;
  border-radius: 12px;
  padding: 18px;
  border: 1px solid #f1f5f9;
}

.vip-privileges h3 {
  margin: 0 0 14px 0;
  font-size: 15px;
  color: #0f172a;
  font-weight: 600;
}

.privilege-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.privilege-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.check-icon {
  color: #f59e0b;
  font-size: 18px;
  margin-top: 2px;
}

.privilege-desc h4 {
  margin: 0 0 2px 0;
  font-size: 13px;
  color: #334155;
  font-weight: 600;
}

.privilege-desc p {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

/* Guidance section */
.vip-guidance {
  text-align: center;
}

.guidance-warning {
  color: #ef4444;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
}

.guidance-info {
  color: #3b82f6;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
}

.contact-box {
  background: #fffbeb;
  border: 1px dashed #fef3c7;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.admin-tip {
  margin: 0;
  font-size: 12px;
  color: #b45309;
}

.vip-btn {
  width: 100%;
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
  border: none;
  font-weight: 600;
}

.vip-btn:hover {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
}
</style>
